import React, { Component, ErrorInfo, ReactNode } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  public props!: Props;
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    
    // Log error to Firestore system_errors collection
    try {
      const errorLog = {
        type: 'react',
        message: error.message || String(error),
        details: errorInfo.componentStack || String(errorInfo),
        userId: auth.currentUser?.uid || 'anonymous',
        userEmail: auth.currentUser?.email || 'none',
        timestamp: new Date().toISOString(),
        resolved: false
      };
      
      addDoc(collection(db, 'system_errors'), errorLog).catch(e => {
        console.error('Failed to log React error to Firestore:', e);
      });
    } catch (e) {
      // Ignore
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)] flex items-center justify-center p-4">
          <div className="max-w-2xl w-full border-4 border-lcars-red rounded-3xl overflow-hidden shadow-2xl">
            <div className="bg-lcars-red text-black p-4 font-bold text-2xl uppercase tracking-widest flex items-center justify-between">
              <span>SYSTEM FAILURE DETECTED</span>
              <span className="animate-pulse">ERROR 47</span>
            </div>
            
            <div className="p-8 space-y-6">
              <p className="text-xl text-lcars-red">The LCARS interface has encountered a critical anomaly.</p>
              
              <div className="bg-white/5 border border-lcars-red p-4 rounded text-sm text-[var(--foreground)]/70 font-mono overflow-auto max-h-60">
                <p className="font-bold text-[var(--foreground)] mb-2">{this.state.error?.message}</p>
                <p className="whitespace-pre-wrap">{this.state.error?.stack}</p>
              </div>
              
              <p className="text-[var(--foreground)]/50 text-sm italic">
                A diagnostic report has been automatically dispatched to Command.
              </p>
              
              <div className="pt-4 flex justify-end">
                <button 
                  onClick={() => window.location.reload()}
                  className="bg-lcars-red text-black px-6 py-3 rounded-full font-bold uppercase tracking-widest hover:bg-white transition-colors"
                >
                  REBOOT INTERFACE
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
